/**
 * Shared, long-lived WebGPU device provider.
 *
 * A `GPUDevice` is expensive to create and is designed to be kept for the page's
 * lifetime and shared across every canvas. This module owns exactly one device
 * for the whole session — effects `acquire()` it rather than each requesting
 * their own (which is what caused context churn and duplicated adapters).
 *
 * The one legitimate reason to rebuild is a genuine `device.lost` (not a
 * deliberate `destroy()`). When that happens the cache is cleared and every
 * registered effect is notified so it can rebuild its GPU resources against a
 * fresh device — this is the *recovery* path, never the routine one.
 */

let cachedDevice: GPUDevice | null = null
let inFlight: Promise<GPUDevice | null> | null = null
const lostListeners = new Set<() => void>()

async function createDevice(): Promise<GPUDevice | null> {
  if (typeof navigator === "undefined" || !navigator.gpu) return null

  const adapter = await navigator.gpu.requestAdapter({ powerPreference: "high-performance" })
  if (!adapter) return null

  const device = await adapter.requestDevice()
  cachedDevice = device

  // Real loss only. A `destroyed` reason is our own teardown and must not
  // trigger recovery (that would loop). Anything else: drop the cache and let
  // effects rebuild against a new device on their next acquire().
  device.lost.then((info) => {
    if (info.reason === "destroyed") return
    if (cachedDevice === device) {
      cachedDevice = null
      inFlight = null
    }
    for (const cb of [...lostListeners]) {
      try {
        cb()
      } catch {
        /* a listener throwing must not stop the others recovering */
      }
    }
  })

  return device
}

/**
 * Returns the shared device, creating it on first call. Returns `null` when
 * WebGPU is unavailable (callers fall back). Concurrent callers share one
 * in-flight request.
 */
export function acquireDevice(): Promise<GPUDevice | null> {
  if (cachedDevice) return Promise.resolve(cachedDevice)
  if (!inFlight) {
    inFlight = createDevice().catch(() => {
      inFlight = null
      return null
    })
  }
  return inFlight
}

/** The synchronously-cached device, if one exists. */
export function peekDevice(): GPUDevice | null {
  return cachedDevice
}

/** Preferred canvas format for this platform. */
export function getPreferredFormat(): GPUTextureFormat {
  return navigator.gpu.getPreferredCanvasFormat()
}

/**
 * Registers a recovery callback fired when the device is lost unexpectedly.
 * Effects use this to rebuild pipelines/buffers against the fresh device.
 * Returns an unsubscribe.
 */
export function onDeviceLost(cb: () => void): () => void {
  lostListeners.add(cb)
  return () => lostListeners.delete(cb)
}

/**
 * Explicitly destroys the shared device. Only for a full app/GPU teardown —
 * individual effects unmounting must NOT call this (the device is shared). The
 * `destroyed` reason is filtered above so this does not trigger recovery.
 */
export function destroySharedDevice(): void {
  cachedDevice?.destroy()
  cachedDevice = null
  inFlight = null
}
